import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useToast } from '../hooks/use-toast';
import api, { formatApiError, getInitials } from '../lib/api';
import {
  Loader2, CalendarClock, Activity, Target, Users as UsersIcon, Crown, Snowflake, Hourglass, Clock,
} from 'lucide-react';

const statusBadge = (s) => ({
  active: 'bg-green-100 text-green-800',
  frozen: 'bg-blue-100 text-blue-800',
  pending: 'bg-amber-100 text-amber-800',
  expired: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
}[s] || 'bg-gray-100 text-gray-700');

const RATING_LABELS = { 1: 'Not Started', 2: 'Beginner', 3: 'Developing', 4: 'Proficient', 5: 'Mastered' };

const MyMembershipPage = ({ user, onOpenLogin }) => {
  const [memberships, setMemberships] = useState([]);
  const [progress, setProgress] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noProfile, setNoProfile] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const [mem, prog, att] = await Promise.all([
        api.get('/portal/memberships'),
        api.get('/portal/progress'),
        api.get('/portal/attendance', { params: { limit: 30 } }),
      ]);
      setMemberships(mem.data);
      setProgress(prog.data);
      setAttendance(att.data);
    } catch (err) {
      if (err?.response?.status === 404) setNoProfile(true);
      else toast({ title: 'Failed to load', description: formatApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [user, toast]);

  useEffect(() => { load(); }, [load]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="mymembership-signin">
        <div className="text-center space-y-3">
          <h2 className="text-2xl font-bold text-gray-900">Please sign in</h2>
          <p className="text-gray-600">Log in with your Member ID to view your membership.</p>
          <button onClick={onOpenLogin} className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-2 rounded-md">Login</button>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen flex justify-center py-32"><Loader2 className="h-10 w-10 animate-spin text-cyan-500" /></div>;

  if (noProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="mymembership-noprofile">
        <div className="text-center space-y-2 max-w-md">
          <h2 className="text-2xl font-bold text-gray-900">No membership linked yet</h2>
          <p className="text-gray-600">Your account isn't linked to a member profile. Please contact the Hurupu Pools front desk.</p>
        </div>
      </div>
    );
  }

  const ratingColor = (r) => (r >= 5 ? 'text-emerald-600' : r >= 4 ? 'text-blue-600' : r >= 3 ? 'text-cyan-600' : r >= 2 ? 'text-amber-600' : 'text-gray-500');

  return (
    <div className="min-h-screen py-12 bg-gradient-to-br from-blue-50 to-cyan-50" data-testid="mymembership-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 flex items-center justify-center text-white text-xl font-bold">
            {getInitials(user?.name)}
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Membership</h1>
            <p className="text-gray-600">Welcome back, {user?.name}!</p>
          </div>
        </div>

        {/* Memberships */}
        <Card className="shadow-md border-0 bg-white">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><CalendarClock className="h-5 w-5" /> My Packages</CardTitle></CardHeader>
          <CardContent>
            {memberships.length === 0 ? (
              <p className="text-sm text-gray-500 py-4" data-testid="mymembership-empty">No active membership. Visit the front desk to enroll.</p>
            ) : (
              <div className="space-y-3" data-testid="mymembership-list">
                {memberships.map((m) => (
                  <div key={m.id} className="border border-gray-100 rounded-xl p-4" data-testid={`my-membership-${m.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="font-semibold text-gray-900">{m.plan_name}</span>
                      <Badge className={statusBadge(m.status)}>
                        {m.status === 'frozen' && <Snowflake className="h-3 w-3 mr-1" />}
                        {m.status === 'pending' && <Hourglass className="h-3 w-3 mr-1" />}
                        {m.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
                      <div><p className="text-xs text-gray-400">Start</p><p className="font-medium text-gray-800">{m.start_date ? m.start_date.slice(0, 10) : '—'}</p></div>
                      <div><p className="text-xs text-gray-400">{m.status === 'frozen' ? 'Days left (paused)' : 'Days remaining'}</p><p className="font-medium text-gray-800">{m.days_remaining != null ? `${m.days_remaining} days` : '—'}</p></div>
                      <div><p className="text-xs text-gray-400">Sessions left</p><p className="font-medium text-gray-800">{m.sessions_remaining != null ? m.sessions_remaining : (m.hours_remaining != null ? `${m.hours_remaining} hrs` : 'Unlimited')}</p></div>
                      <div><p className="text-xs text-gray-400">Members</p><p className="font-medium text-gray-800">{m.total_pax} pax</p></div>
                    </div>
                    {m.status === 'pending' && <p className="text-xs text-amber-600 mt-2">Queued — activates when your current package ends.</p>}
                    {m.members?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {m.members.map((mm) => (
                          <Badge key={mm.swimmer_id} variant="secondary" className="text-xs">
                            {mm.is_leader && <Crown className="h-3 w-3 mr-1 text-amber-500" />}{mm.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Progress */}
        <Card className="shadow-md border-0 bg-white">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Target className="h-5 w-5" /> My Progress</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-cyan-600" data-testid="my-skill-score">{progress?.summary?.overall_average ?? 0}<span className="text-base text-gray-400">/5</span></p>
                <p className="text-xs text-gray-500">Skill Score</p>
              </div>
              <div className="text-sm text-gray-500">
                <p>{progress?.summary?.assessments_count ?? 0} assessment{(progress?.summary?.assessments_count ?? 0) !== 1 ? 's' : ''}</p>
                {progress?.summary?.last_assessed_date && <p>Last: {progress.summary.last_assessed_date}</p>}
              </div>
            </div>
            {progress?.summary?.skills?.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {progress.summary.skills.map((s) => (
                  <div key={s.skill}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-700">{s.skill}</span>
                      <span className={`text-xs font-medium ${ratingColor(s.rating)}`}>{RATING_LABELS[s.rating]}</span>
                    </div>
                    <Progress value={(s.rating / 5) * 100} className="h-2" />
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-500">No assessments yet. Your coach will add them soon.</p>}
            {progress?.summary?.next_goals && (
              <div className="flex items-start gap-2 bg-cyan-50 rounded-lg p-3">
                <Target className="h-4 w-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                <div><p className="text-xs font-medium text-cyan-700">Next Goals</p><p className="text-sm text-gray-700">{progress.summary.next_goals}</p></div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Attendance */}
        <Card className="shadow-md border-0 bg-white">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Activity className="h-5 w-5" /> My Attendance</CardTitle></CardHeader>
          <CardContent>
            {attendance.length === 0 ? <p className="text-sm text-gray-500 py-2">No classes attended yet.</p> : (
              <div className="space-y-2" data-testid="my-attendance-list">
                {attendance.map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                    <span className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-cyan-500" />{a.date} · in {a.check_in_time?.slice(11, 16)}{a.check_out_time ? ` · out ${a.check_out_time.slice(11, 16)}` : ''}</span>
                    <Badge variant="secondary" className="text-xs">{a.method}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MyMembershipPage;
